<script>
    import { createEventDispatcher, onMount, onDestroy } from "svelte";
    import { PowerTable } from "@muonw/powertable";
    import * as utils from "../js/utils.js";
    import { writable } from "svelte/store";
    import { locale, format } from "svelte-i18n";
    import dayjs from "dayjs";
    import relativeTime from "dayjs/plugin/relativeTime";
    import localizedFormat from "dayjs/plugin/localizedFormat";
    import TableReference from "./table-reference.svelte";
    import { notificate } from "../services/notification.js";
    import "dayjs/locale/es";
    import "dayjs/locale/en";

    dayjs.locale($locale);
    dayjs.extend(relativeTime);
    dayjs.extend(localizedFormat);

    let {
        data = [],
        columns = [],
        parseFunc = (x) => x,
        exportsName = "",
        list = "",
        certsByAssetKind = {}, // For certificate charts
        issuerTopFive = {},   // For certificate charts
    } = $props();

    let infoBtn;
    let tableData = writable([]);

    const dispatch = createEventDispatcher();

    let ptData = data;
    let ptInstructs = columns;
    let tableElement = $state();

    const exportToJSON = (e) => {
        let currentData = tableElement.getData().data;
        // Download JSON object as a file from browser (https://stackoverflow.com/a/30800715)
        let dataStr =
            "data:text/json;charset=utf-8," +
            encodeURIComponent(JSON.stringify(currentData));
        let downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute(
            "download",
            `${exportsName}_vulseek.json`,
        );
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    const exportToCSV = async () => {
        let instructs = ptInstructs;
        let header = [];
        let titles = [];
        let items = tableElement.getData().data;

        for (let item of items) {
            if (item.asset_kind) {
                item.asset_kind = $format(
                    `asset.kind.${item.asset_kind}.singular`,
                );
            }
            if (item.expiration_date) {
                item.expiration_date = getDateLabels(
                    item["expiration_date"],
                ).expiration_date;
            }
        }

        const replacer = (key, value) => (value === null ? "" : value); // specify how you want to handle null values here

        instructs.forEach((col) => {
            header.push({ key: col.key, value: col.title });
            if (
                col.title === "Expira en" ||
                col.title === "Válido hasta" ||
                col.title === "Válido desde"
            )
                col.title = col.title.replace(" ", "_");
            if (col.title === "Tipo de activo") col.title = "Tipo_de_activo";
            titles.push(col.title);
        });

        const csv = [
            titles.join(";"), // header row first
            ...items.map((row) =>
                header
                    .map((fieldName) =>
                        JSON.stringify(row[fieldName["key"]], replacer),
                    )
                    .join(";"),
            ),
        ].join("\r\n");

        let hiddenElement = document.createElement("a");
        hiddenElement.href =
            "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
        hiddenElement.target = "_blank";
        hiddenElement.download = `${exportsName}_vulseek.csv`;
        hiddenElement.click();
    };

    const getDateLabels = (date) => {
        let expiration_date;
        let expired_label;
        let now = dayjs();

        if (dayjs(date) < now) {
            let diff = dayjs(date).diff(now, "months");
            expired_label =
                $format("date_labels.from") +
                Math.abs(diff) +
                $format("date_labels.months");

            if (diff == 0) {
                diff = dayjs(date).diff(now, "days");
                expired_label =
                    $format("date_labels.from") +
                    Math.abs(diff) +
                    $format("date_labels.days");
            }
            if (diff > 12) {
                diff = dayjs(date).diff(now, "years");
                expired_label =
                    $format("date_labels.from") +
                    Math.abs(diff) +
                    $format("date_labels.years");
            }
            expiration_date = $format("expired");
        } else {
            expiration_date = dayjs(now).to(date);
        }

        return {
            expiration_date: expiration_date,
            expired_label: expired_label,
        };
    };

    const showContent = (kind, id) => {
        let visibleElement = document.getElementById(kind + "-visible-" + id);
        let hiddenElement = document.getElementById(kind + "-hidden-" + id);
        visibleElement.classList.remove("is-visible");
        visibleElement.classList.add("is-hidden");
        hiddenElement.classList.remove("is-hidden");
        hiddenElement.classList.add("is-visible");
    };

    const hideContent = (kind, id) => {
        let visibleElement = document.getElementById(kind + "-visible-" + id);
        let hiddenElement = document.getElementById(kind + "-hidden-" + id);
        visibleElement.classList.add("is-visible");
        visibleElement.classList.remove("is-hidden");
        hiddenElement.classList.add("is-hidden");
        hiddenElement.classList.remove("is-visible");
    };

    /* START COPY TO CLIPBOARD FUNCTION */

    const copyTableToClipboard = async () => {
        try {
            // Determine current language
            const currentLang = $locale || 'es';
            
            // Get current table data
            let items = tableElement.getData().data;
            
            // Apply the same formatting as exportToCSV
            for (let item of items) {
                if (item.asset_kind) {
                    item.asset_kind = $format(
                        `asset.kind.${item.asset_kind}.singular`,
                    );
                }
                if (item.expiration_date) {
                    item.expiration_date = getDateLabels(
                        item["expiration_date"],
                    ).expiration_date;
                }
            }
            
            // Prepare combined data object
            const combinedData = {};
            
            // Add table data
            combinedData[currentLang === 'es' ? 'certificados' : 'certificates'] = items;
            
            // Add chart data if available (for certificates)
            if (Object.keys(certsByAssetKind).length > 0 || Object.keys(issuerTopFive).length > 0) {
                const chartData = {};
                
                // Add certs by asset kind data
                if (Object.keys(certsByAssetKind).length > 0) {
                    chartData[currentLang === 'es' ? 'certificados_por_tipo_de_activo' : 'certificates_by_asset_kind'] = certsByAssetKind;
                }
                
                // Add issuer top five data
                if (Object.keys(issuerTopFive).length > 0) {
                    chartData[currentLang === 'es' ? 'emisores_principales' : 'top_issuers'] = issuerTopFive;
                }
                
                combinedData[currentLang === 'es' ? 'gráficos' : 'charts'] = chartData;
            }
            
            // Add export date
            combinedData[currentLang === 'es' ? 'fecha_de_exportación' : 'export_date'] = new Date().toISOString();
            
            // Format as JSON
            const jsonData = JSON.stringify(combinedData, null, 2);
            
            // Copy to clipboard
            await navigator.clipboard.writeText(jsonData);
            
            // Show success notification
            notificate("request_ok", "toast", {
                data: $format("buttons.copied_to_clipboard")
            });
        } catch (err) {
            // Show error notification
            notificate("save_error", "toast", {
                data: $format("buttons.copy_to_clipboard_failed")
            });
        }
    };

    /* END COPY TO CLIPBOARD FUNCTION */

    let customSearch = (event) => {
        let searchInput = event.target.value;
        let input = utils.normalizeString(searchInput);
        let result = JSON.parse(JSON.stringify(data));

        result = result.filter((item) => {
            if (item.name) {
                if (utils.normalizeString(item.name).includes(input)) {
                    return true;
                }
            }
            if (item.cert_subject_cn) {
                if (
                    utils.normalizeString(item.cert_subject_cn).includes(input)
                ) {
                    return true;
                }
            }
            if (item.cert_issuer_cn) {
                if (
                    utils.normalizeString(item.cert_issuer_cn).includes(input)
                ) {
                    return true;
                }
            }
            if (item.cert_san) {
                let san = item.cert_san.split(",");
                san = san.join(" ");
                if (san.includes(input)) {
                    return true;
                }
            }
            if (item.location) {
                if (utils.normalizeString(item.location).includes(input)) {
                    return true;
                }
            }
            if (item.asset_name) {
                if (utils.normalizeString(item.asset_name).includes(input)) {
                    return true;
                }
            }
            if (item.asset_kind) {
                let assetKind = $format(
                    `asset.kind.${item.asset_kind}.singular`,
                );
                if (utils.normalizeString(assetKind).includes(input)) {
                    return true;
                }
            }
            if (item.cert_issued) {
                let date = dayjs(item.cert_issued).format("YYYY-MM-DD");
                if (date.includes(input)) {
                    return true;
                }
            }

            if (item.cert_expires) {
                let date = dayjs(item.cert_expires).format("YYYY-MM-DD");
                let expired_label = getDateLabels(item.cert_expires);
                if (date.includes(input)) {
                    return true;
                }
                if (
                    utils
                        .normalizeString(expired_label.expiration_date)
                        .includes(utils.normalizeString(input))
                ) {
                    return true;
                }
            }
            if (item.kind) {
                if (utils.normalizeString(item.kind).includes(input)) {
                    return true;
                }
            }
        });
        result = result;
        tableData.update((tdata) => (tdata = result));
    };

    let ptOptions = {
        uniquePrefix: "listPropertiesTable",
        rowsPerPageOptions: [10, 20, 50, 100],
        rowsPerPage: 10,
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: {
            add: $format("list.interface.add"),
            delete: $format("list.interface.delete"),
            filterBy: $format("list.interface.filterBy"),
            from: $format("list.interface.from"),
            invertSelection: $format("list.interface.invertSelection"),
            next: $format("list.interface.next"),
            of: $format("list.interface.of"),
            previous: $format("list.interface.previous"),
            rows: $format("list.interface.rows"),
            search: $format("list.interface.search"),
            selectAll: $format("list.interface.selectAll"),
            selectNone: $format("list.interface.selectNone"),
        },
        isDataRemote: false,
        userFunctions: {
            customParse: parseFunc,
        },
    };

    onMount(() => {
        tableData.update((tdata) => data);
    });

    window.showContent = showContent;
    window.hideContent = hideContent;
</script>

<div class="card">
    <div class="card-header">
        <div class="float-start">
            <TableReference list={"certificate"} columnsDefinition={columns} />
        </div>
        <div class="float-end">
            <button
                class="btn btn-secondary table-button btn-refresh btn-xs t-refresh-listProperties-btn"
                tabindex="0"
                aria-controls="tagTable"
                type="button"
                title={$format("buttons.refresh")}
                id="propsListRefreshBtn"
                onclick={() => dispatch("refresh")}
                ><i class="fa fa-sync-alt"></i></button
            >
            <button
                class="btn btn-secondary table-button btn-export btn-xs t-json-props-list-btn"
                tabindex="0"
                aria-controls="tagTable"
                type="button"
                title={$format("buttons.json")}
                id="propsListJSONbtn"
                onclick={exportToJSON}
                ><i class="fab fa-js-square" style="font-size: 18px;"
                ></i></button
            >
            <button
                class="btn btn-secondary table-button btn-export btn-xs t-csv-props-list-btn"
                tabindex="0"
                aria-controls="tagTable"
                type="button"
                title={$format("buttons.csv")}
                id="propsListCSVbtn"
                onclick={exportToCSV}
                ><i class="fa fa-file-csv" style="font-size: 18px;"></i></button
            >
            <button
                class="btn btn-secondary table-button btn-export btn-xs t-copy-props-list-btn"
                tabindex="0"
                aria-controls="tagTable"
                type="button"
                title={$format("buttons.copy_to_clipboard")}
                id="propsListCopyBtn"
                onclick={copyTableToClipboard}
                ><i class="fa fa-copy" style="font-size: 18px;"></i></button
            >
        </div>
    </div>
    <div class="card-body">
        <div class="table-responsive">
            <div
                class="mb-5 d-flex justify-content-between flex-column"
                style="gap: 15px;"
            >
                <form class="form-inline" novalidate>
                    <div class="input-group input-group-sm">
                        <input
                            class="form-control custom-search"
                            placeholder={$format("filter")}
                            data-name="search-input"
                            type="text"
                            oninput={customSearch}
                        />
                        <div class="input-group-append">
                            <i class="fas fa-search"></i>
                        </div>
                    </div>
                </form>
            </div>
            <PowerTable
                ptData={$tableData}
                {ptOptions}
                {ptInstructs}
                bind:this={tableElement}
            >
                {#snippet noResults()}
                    <div>
                        {$format("results_not_found")}
                    </div>
                {/snippet}
            </PowerTable>
        </div>
    </div>
</div>

<style>
    .custom-search {
        background-color: whitesmoke;
        border-color: #e8e8e8;
        color: rgba(52, 58, 64, 0.8);
    }
    .input-group-append {
        position: absolute;
        right: 25px;
        top: 0.5em;
        z-index: 99999;
    }
    .input-group-append .fas.fa-search {
        color: rgba(52, 58, 64, 0.8);
    }
    .input-group {
        width: 25%;
        position: relative;
    }
</style>
